import { proxyCustomElement, HTMLElement, h, Fragment, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, u as updateElementStyle, d as isNumber, f as isUnset, n as numToRem } from './utils.js';

// Constants
const component = 'cl-loader';

const clLoaderCss = ".sc-cl-loader-h{display:block;font-family:var(--font-family-brand);font-variant-numeric:lining-nums;position:relative;-webkit-user-select:none;-moz-user-select:none;user-select:none}.circular.sc-cl-loader-h{aspect-ratio:1/1;height:var(--custom-loader-size, var(--loader-size, 100%));position:relative;stroke-width:calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6))));width:var(--custom-loader-size, var(--loader-size, 100%))}.circular.sc-cl-loader-h .loader__slot.sc-cl-loader{font-size:calc(var(--custom-loader-size, var(--loader-size, 100%)) * 0.25);max-height:100%;max-width:100%;position:absolute}.sc-cl-loader-h.circular .sc-cl-loader-s>*{--typography-size:var(--loader-typography-size, calc(var(--custom-loader-size, var(--loader-size, 100%)) * 0.25));margin:0}.circular.sc-cl-loader-h .loader__circle.sc-cl-loader{fill:transparent;stroke-linecap:round;transform:rotate(-90deg);width:100%}.circular.sc-cl-loader-h .loader__circle__wrapper.sc-cl-loader{align-items:center;display:flex;justify-content:center;position:relative}.circular.sc-cl-loader-h .loader__background.sc-cl-loader{stroke:rgba(var(--color-brand-200))}.circular.sc-cl-loader-h .loader__progress.sc-cl-loader{stroke:rgba(var(--color-brand-main))}.linear.sc-cl-loader-h{width:var(--custom-loader-size, var(--loader-size, 100%))}.linear.sc-cl-loader-h .loader__slot.sc-cl-loader{display:flex;flex-direction:row-reverse;font-size:var(--font-size-subtitle-2);width:100%}.linear.sc-cl-loader-h .loader__bar.sc-cl-loader{display:flex;position:relative;width:100%}.linear.sc-cl-loader-h .loader__background.sc-cl-loader,.linear.sc-cl-loader-h .loader__progress.sc-cl-loader{height:calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6))))}.linear.sc-cl-loader-h .loader__background.sc-cl-loader{background-color:rgba(var(--color-brand-200));border-color:transparent;border-radius:var(--border-radius-interactive-large);margin-top:var(--spacing-half);width:100%}.linear.sc-cl-loader-h .loader__progress.sc-cl-loader{background-color:rgba(var(--color-brand-main));border-radius:var(--border-radius-interactive-large);bottom:0;left:0;position:absolute;width:90%}.linear.loader--right.sc-cl-loader-h{display:flex;flex-direction:row-reverse}.linear.loader--right.sc-cl-loader-h .loader__background.sc-cl-loader{bottom:50%;left:0;position:absolute}.linear.loader--right.sc-cl-loader-h .loader__progress.sc-cl-loader{bottom:50%}.linear.loader--right.sc-cl-loader-h .loader__bar.sc-cl-loader{position:relative;width:100%}.linear.loader--right.sc-cl-loader-h .loader__slot.sc-cl-loader{margin-bottom:calc((var(--spacing-half)) + calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6)))));margin-top:0;padding-left:var(--spacing-half);width:-moz-fit-content;width:fit-content}.linear.loader--right.sc-cl-loader-h .loader__slot.sc-cl-loader *.sc-cl-loader{margin-top:0}.error.circular.sc-cl-loader-h .loader__background.sc-cl-loader{stroke:rgba(var(--system-color-error-main))}.error.circular.sc-cl-loader-h .loader__progress.sc-cl-loader{stroke:rgba(var(--system-color-error-main))}.error.sc-cl-loader-h .loader__background.sc-cl-loader,.error.sc-cl-loader-h .loader__progress.sc-cl-loader{background-color:rgba(var(--system-color-error-main))}.sc-cl-loader-h .loader__background--indeterminate.sc-cl-loader{height:calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6))));overflow:hidden;position:absolute}.sc-cl-loader-h .loader__background--indeterminate.sc-cl-loader::before{animation:increase 2s infinite;background-color:rgba(var(--color-brand-main));border-radius:var(--border-radius-large);content:\" \";height:calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6))));position:absolute}@keyframes increase{from{left:-15%;width:15%}to{left:105%;width:80%}}.sc-cl-loader-h .loader__background--indeterminate.sc-cl-loader::after{animation:decrease 2s 0.5s infinite;background-color:rgba(var(--color-brand-main));border-radius:var(--border-radius-large);content:\" \";height:calc(1 / 8 * var(--custom-loader-thickness, var(--loader-thickness, var(--size-6))));position:absolute}@keyframes decrease{from{left:-80%;width:80%}to{left:110%;width:10%}}.sc-cl-loader-h .loader__circle--indeterminate.sc-cl-loader{animation:rotate 1.6s cubic-bezier(0.85, 0.05, 0.15, 0.95) infinite}@keyframes rotate{from{transform:rotate(-108deg)}to{transform:rotate(252deg)}}";

const ClLoader$1 = /*@__PURE__*/ proxyCustomElement(class ClLoader extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Returns the circular component.
     */
    this.getCircularComponent = () => {
      const thickness = this.thickness && isNumber(this.thickness) ? this.thickness : 6;
      const radius = 42 - thickness / 2;
      const cx = 44;
      const cy = 44;
      const circumference = radius * 2 * Math.PI;
      const percentage = this.indeterminate ? 0.1 : this.value;
      const offset = circumference - percentage * circumference;
      const viewBox = '0 0 88 88';
      return (h("div", { class: 'loader__circle__wrapper' }, h("svg", { class: cl('loader__circle', { 'loader__circle--indeterminate': this.indeterminate }), viewBox: viewBox }, h("circle", { class: 'loader__background', cx: cx, cy: cy, r: radius }), h("circle", { class: 'loader__progress', cx: cx, cy: cy, r: radius, "stroke-dasharray": `${circumference} ${circumference}`, style: { strokeDashoffset: offset.toString() } })), h("div", { ref: (el) => (this._loaderSlotElement = el), class: 'loader__slot' }, h("slot", { name: 'label' }))));
    };
    /**
     * Returns the linear component.
     */
    this.getLinearComponent = () => (h(Fragment, null, h("div", { ref: (el) => (this._loaderSlotElement = el), class: 'loader__slot' }, h("slot", { name: 'label' })), h("div", { class: 'loader__bar' }, h("div", { class: cl('loader__background', { 'loader__background--indeterminate': this.indeterminate }) }), h("div", { class: 'loader__progress', style: {
        width: `${this.indeterminate ? 0 : this.value * 100}%`,
      } }))));
    this.circular = false;
    this.error = false;
    this.indeterminate = false;
    this.labelRight = false;
    this.size = undefined;
    this.thickness = 6;
    this.value = 0;
  }
  /**
   * Validates the size property and updates the styles accordingly
   */
  handleSize(size) {
    var _a;
    const cssVariable = '--custom-loader-size';
    switch (true) {
      case isUnset(size):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(size):
        this.logger.error.attribute.size(`Given invalid value "${size}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(size), cssVariable);
        break;
    }
    if (this.circular) {
      const { fontSize } = getComputedStyle(this.element);
      const { height } = this.element.getBoundingClientRect();
      this._loaderSlotElement.style.setProperty('--loader-typography-size', `${height / 4 / +((_a = (fontSize || '16px')) === null || _a === void 0 ? void 0 : _a.replace('px', ''))}rem`);
    }
  }
  /**
   * Validates the thickness property and updates the styles accordingly
   */
  handleThickness(thickness) {
    const cssVariable = '--custom-loader-thickness';
    switch (true) {
      case isUnset(thickness):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(thickness):
        this.logger.error.attribute.thickness(`Given invalid value "${thickness}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(thickness), cssVariable);
        break;
    }
  }
  /**
   * Validates the value property
   */
  validateValue(value) {
    if (!isNumber(+value)) {
      this.logger.error.attribute.value(`Given invalid value "${value}".`);
    }
    if (+value < 0 || +value > 1) {
      this.logger.error.attribute.value(`Given invalid value "${value}".`, 'It should be between 0 and 1.');
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Validate the thickness attribute and update the styles;
   * - Validate the value attribute;
   */
  connectedCallback() {
    this.handleThickness(this.thickness);
    this.validateValue(this.value);
  }
  /**
   * Just once before the component has fully loaded and rendered:
   * - Validate whether the size for the circular variant is set;
   */
  componentWillLoad() {
    if (this.circular && !this.size) {
      this.size = 10;
    }
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Validate the size attribute and update the styles;
   */
  componentDidLoad() {
    this.handleSize(this.size);
  }
  render() {
    const { circular } = this;
    return (h(Host, { class: cl('loader', circular ? 'circular' : 'linear ', {
        error: this.error,
        'loader--right': !circular && this.labelRight,
      }) }, circular ? this.getCircularComponent() : this.getLinearComponent()));
  }
  get element() { return this; }
  static get watchers() { return {
    "size": ["handleSize"],
    "thickness": ["handleThickness"],
    "value": ["validateValue"]
  }; }
  static get style() { return clLoaderCss; }
}, [6, "cl-loader", {
    "circular": [4],
    "error": [4],
    "indeterminate": [4],
    "labelRight": [4, "label-right"],
    "size": [1026],
    "thickness": [2],
    "value": [2]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-loader"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-loader":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClLoader$1);
      }
      break;
  } });
}

const ClLoader = ClLoader$1;
const defineCustomElement = defineCustomElement$1;

export { ClLoader, defineCustomElement };

//# sourceMappingURL=cl-loader.js.map